import {
  CheckBox,
  CheckBoxOutlineBlank,
  ChevronLeft
} from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  ImageList,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import AvatarWithLoader from 'components/avatar-with-loader'
import NivosCommentSection from 'components/comment-section'
import IncidentReportActionButton from 'components/incident-reports/incident-report-action-button'
import {
  Operations as IncidentReportOperations,
  operations as incidentReportOperations
} from 'states/dux/incident-report/operations'
import { Payload as IncidentReportPayload } from 'states/dux/incident-report/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import State from 'states/types'
import { IncidentReport } from 'types/incident-report'
import {
  get,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  incidentReport: IncidentReportPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    incidentReport: IncidentReportOperations
  }
}

type AppProps = StateProps & DispatchProps

const IncidentReportDetailFC: React.FC<any> = (props: AppProps) => {
  const { operations, incidentReport, theme } = props
  const [report, setReport] = React.useState<IncidentReport>()
  const [trackedLoading, setTrackedLoading] = React.useState(false)

  const routeParams = useParams()
  const steps = ['Pending', 'Accepted', 'Assigned', 'In-Progress', 'Resolved']

  const setReportFromParams = (): void => {
    if (routeParams?.id != null) {
      const reportId = Number(routeParams?.id)
      setReport(incidentReport.data.find(i => i.id === reportId))
    }
  }

  const toggleTrack = (): void => {
    setTrackedLoading(true)

    if (report === undefined) {
      return
    }

    void post(
      `/report/${report.id}/`,
      { tracked: !report.tracked },
      false,
      false,
      'PUT'
    ).then(data => {
      operations.incidentReport.update(objectifyDateStringFields(data))
      setTrackedLoading(false)
      operations.incidentReport.loading(false)
    })
  }

  React.useEffect(() => {
    if (incidentReport.data.length < 1) {
      operations.incidentReport.loading(true)
      void get('/report/').then((data: IncidentReport[]) => {
        const parsedData = data.map(report => {
          return objectifyDateStringFields(report)
        })
        operations.incidentReport.set(parsedData)
        operations.incidentReport.loading(false)
      })
    }
  }, [])

  React.useEffect(() => setReportFromParams(), [incidentReport])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      width={'1'}
      sx={{ minHeight: 'calc(100vh - 80px)' }}
      padding={10}
    >
      <Box display={'flex'} alignItems={'center'}>
        <IconButton sx={{ fontSize: 48 }} onClick={() => window.history.back()}>
          <ChevronLeft fontSize={'inherit'} />
        </IconButton>
        <Typography variant={'h3'}>Back</Typography>
      </Box>

      <Box paddingY={3} />

      {report !== undefined && (
        <>
          <Box display={'flex'} alignItems={'center'} padding={1}>
            <Typography variant={'h3'} padding={1} marginRight={1}>
              <strong>{report.title}</strong>
            </Typography>
            <Chip
              sx={{
                height: 'auto',
                marginRight: 6,
                '& .MuiChip-label': {
                  display: 'block',
                  whiteSpace: 'normal',
                  paddingY: 0.5,
                  paddingX: 3
                }
              }}
              color={'secondary'}
              variant={'filled'}
              label={
                <Typography variant={'h6'} color={props.theme.primaryColor}>
                  {report.status}
                </Typography>
              }
              size={'medium'}
            />
            <Box flexGrow={1} />
            <Button
              variant={'outlined'}
              size={'large'}
              endIcon={
                trackedLoading ? (
                  <CircularProgress size={22} />
                ) : report.tracked ? (
                  <CheckBox />
                ) : (
                  <CheckBoxOutlineBlank />
                )
              }
              sx={{ marginRight: 4 }}
              onClick={() => toggleTrack()}
            >
              <Typography variant={'h5'}>Track</Typography>
            </Button>
            <IncidentReportActionButton report={report} />
          </Box>

          <Box paddingY={3} />

          <Box display={'flex'} paddingX={2} minHeight={350}>
            <Box
              display={'flex'}
              flexDirection={'column'}
              flexGrow={1}
              height={1}
            >
              <Typography variant={'h4'}>Description</Typography>
              <Box
                display={'flex'}
                flexDirection={'column'}
                alignItems={'flex-start'}
                border={2}
                borderRadius={6}
                padding={3}
                marginY={2}
                flexGrow={1}
                borderColor={'#d4d2d2'}
              >
                <Typography fontSize={22}>{report.description}</Typography>
              </Box>
            </Box>
            <Box
              marginRight={1}
              marginLeft={4}
              width={650}
              height={1}
              display={'flex'}
              flexDirection={'column'}
            >
              <Typography variant={'h4'}>Details</Typography>
              <Box
                display={'flex'}
                border={2}
                borderRadius={6}
                padding={3}
                marginY={2}
                flexGrow={1}
                borderColor={'#d4d2d2'}
              >
                <Box paddingX={1}>
                  <Typography fontSize={22} gutterBottom>
                    Location
                  </Typography>
                  <Typography fontSize={22} gutterBottom>
                    Reporter
                  </Typography>
                  <Typography fontSize={22} gutterBottom>
                    Date
                  </Typography>
                  <Typography fontSize={22} gutterBottom>
                    Taken Action
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize={22} gutterBottom>
                    : <u>{report.location}</u>
                  </Typography>
                  <Typography fontSize={22} gutterBottom>
                    : <u>{report.createdBy.fullName}</u>
                  </Typography>
                  <Typography fontSize={22} gutterBottom>
                    : <u>{report.createdAt.toUTCString().slice(0, 16)}</u>
                  </Typography>
                  <Typography fontSize={22} gutterBottom>
                    : <u>{report.requiredAction}</u>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box padding={2}>
            <Box
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              height={180}
            >
              <Stepper
                sx={{
                  '.MuiStepConnector-line': {
                    borderTopWidth: 2
                  },
                  '.MuiStepLabel-label': {
                    color: theme.secondaryColor
                  },
                  '.Mui-disabled': {
                    '.MuiStepIcon-root': {
                      color: theme.quaternaryColor
                    },
                    '.MuiStepIcon-text': {
                      fill: theme.secondaryColor
                    }
                  },
                  '.Mui-completed': {
                    '.MuiStepConnector-line': {
                      borderTopWidth: 2,
                      borderColor: '#6cd46a'
                    },
                    '.MuiStepIcon-root': {
                      color: '#6cd46a'
                    },
                    '.MuiStepLabel-label': {
                      color: '#6cd46a'
                    }
                  },
                  width: 1
                }}
                activeStep={
                  report.status === 'Declined'
                    ? 6
                    : steps.indexOf(report.status) + 1
                }
                alternativeLabel
              >
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel>
                      {step}
                      {steps.indexOf(report.status) >= index && (
                        <Typography fontSize={14}>
                          {new Date().toUTCString().slice(0, 16)}
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                ))}
                {report.status === 'Declined' && (
                  <Step>
                    <StepLabel>Declined</StepLabel>
                  </Step>
                )}
              </Stepper>
            </Box>
          </Box>

          <Box display={'flex'} padding={2}>
            <Box flexGrow={1}>
              <Typography variant={'h4'} padding={1}>
                Comments
              </Typography>
              <Box
                display={'flex'}
                padding={1}
                border={2}
                borderColor={'#d4d2d2'}
                borderRadius={6}
                justifyContent={'center'}
                alignItems={'center'}
                minHeight={300}
              >
                <NivosCommentSection
                  comments={report.comments}
                  maxCommentHeight={500}
                  submitFnCallback={comment => {
                    void post(`/report/${report.id}/add_comment/`, {
                      comment
                    }).then(data => {
                      const newReport = objectifyDateStringFields(data)
                      operations.incidentReport.update(newReport)
                    })
                  }}
                  containerProps={{
                    sx: {
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      width: 1,
                      minHeight: 300,
                      padding: 4
                    }
                  }}
                />
              </Box>
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              width={600}
              marginLeft={4}
            >
              <Typography variant={'h4'}>Attachments</Typography>
              {report.images.length > 0 && (
                <ImageList cols={2} rowHeight={300}>
                  {report.images.map((img, index) => (
                    <AvatarWithLoader
                      key={index}
                      src={img.url}
                      avatarProps={{
                        sx: { borderRadius: 2, height: 300, width: 'auto' }
                      }}
                    />
                  ))}
                </ImageList>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    incidentReport: state.incidentReport
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  incidentReport: incidentReportOperations
})

const IncidentReportDetail = connect(
  mapStateToProps,
  mapDispatchToProps
)(IncidentReportDetailFC) as React.FC

export default IncidentReportDetail
